import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { MaterialModule } from './material-modules';
import { NotificationService } from './services/notification.service';
import { ModalService } from './services/modal.service';
import { HelpersService } from './services/helpers.service';
import { NgModule, InjectionToken } from '@angular/core';
import { CommonModule, DatePipe, LowerCasePipe, CurrencyPipe } from '@angular/common';
import store from './store';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS } from '@angular/material/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule, Title } from '@angular/platform-browser';
import { AgGridModule } from 'ag-grid-angular';
import { AccountSummaryComponent } from './components/account-summary/account-summary.component';
import { CloseItemOutComponent } from './components/close-item-out/close-item-out.component';
import { ContractsComponent } from './components/contracts/contracts.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { InvoicesComponent } from './components/invoices/invoices.component';
import { ItemsOutComponent } from './components/items-out/items-out.component';
import { PageSidebarComponent } from './components/page-sidebar/page-sidebar.component';
import { TrackingComponent } from './components/tracking/tracking.component';
import { TrackService } from './services/track.service';
import { CustomerPortalComponent } from './components/customer-portal/customer-portal.component';
import { ConsumerLoginLanguage, ConsumerLoginModule } from '@por/consumer-login/ui-lib';
import { SharedModule } from './shared/shared.module';
import { AppDateAdapter, APP_DATE_FORMATS } from './adaptors/format-date-picker';
import { LoggerService } from './services/logger.service';
import { devModules } from './store-dev-tools';
import { ConsumerRedirectionComponent } from './components/consumer-redirection/consumer-redirection.component';
import { BackendInterceptor } from './services/backend.interceptor';
import { RedirectionService } from './services/redirection.service';
import { loginDefaults } from '../input-config/login-config';
export { CONSUMER_PORTAL_APP_CONFIG } from './models/consumer-portal-config';
import { TranslateLoader, TranslateModule, MissingTranslationHandler } from '@ngx-translate/core';
import { UIMissingTranslationHandler, UITranslateLoader } from '@por/shared/ui/translations';
/* eslint-disable @typescript-eslint/naming-convention  */
import * as EnUSTranslations from '../assets/en-US.json';
import * as EnGBTranslations from '../assets/en-GB.json';
import * as EnAUTranslations from '../assets/en-AU.json';
import * as EsESTranslations from '../assets/es-ES.json';
import * as FrCATranslations from '../assets/fr-CA.json';
import * as FrFRTranslations from '../assets/fr-FR.json';
import { Language } from '@por/shared/core';
import { MatMenuModule } from '@angular/material/menu';
import { LanguageSelectorComponent } from './components/language-selector/language-selector.component';
import { ItemsOutActionComponent } from './components/items-out-action/items-out-action-component';
import { PaymentModule } from './payment/payment.module';
import { OrganizationIdService } from './services/organization-id.service';
import { SubdomainRedirectionComponent } from './components/subdomain-redirection/subdomain-redirection.component';
import { ContractFullWidthRowComponent } from './components/contract-full-width-row/contract-full-width-row.component';
import { ItemsOutFullWidthRowComponent } from './components/items-out-full-width-row/items-out-full-width-row.component';
import { InvoicesFullWidthRowComponent } from './components/invoices-full-width-row/invoices-full-width-row.component';
import { HamburgerComponent } from './components/hamburger/hamburger.component';
import { RentalRequestModalComponent } from './components/rental-request-modal/rental-request-modal.component';
import { ContractPaymentHistoryModalComponent } from './components/contract-detail/contract-payment-history-modal/contract-payment-history-modal.component';
import { PorFormsModule, ApxInputModule, ApxRadioModule, ApxButtonModule } from '@por/shared/ui';
import { ContractDetailComponent } from './components/contract-detail/contract-detail.component';
import { FilterLineItemStatusPipe } from './pipes/filter-line-item-status.pipe';
import { FormLineItemNamePipe } from './pipes/form-line-item-name.pipe';
import { ItemsOutDetailRendererComponent } from './components/items-out/renderer/items-out-detail-renderer/items-out-detail-renderer.component';
import { MapActionsService } from './services/map-action.service';
import { InvoiceDetailComponent } from './components/invoice-detail/invoice-detail.component';
import { RequestCellRendererComponent } from './components/items-out/renderer/request-cell-renderer';
import { ItemsOutActionsMenuComponent } from './components/items-out/renderer/items-out-actions-menu.component';
import { BasicConsumerPortalComponent } from './basic/basic-consumer-portal/basic-consumer-portal.component';
import { FormatNumberPipe } from './pipes/format-number.pipe';
import { ServiceRequestComponent } from './components/service-request/service-request.component';
import { ProductDetailComponent } from './components/product-detail/product-detail.component';
import { QuickLinkComponent } from './quick-link/quick-link.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { DEFAULT_LOCALE } from './constants/default.const';
import { CustomerEffects } from './store/domains/customer/customer.effects';
import { BookAgainContractModalComponent } from './components/clone-contract-modal/book-again-contract-modal/book-again-contract-modal.component';
import { BookAgainContractRequestFormComponent } from './components/clone-contract-modal/book-again-contract-request-form/book-again-contract-request-form.component';
import { StringifyPipe } from './pipes/stringify.pipe';
import { SelectedItemActionsComponent } from './components/selected-item-actions/selected-item-actions.component';
import { FormatDatePipe } from './pipes/format-date.pipe';
import { DepotTimezoneDatePipe } from './pipes/depot-timezone-date.pipe';
import { DocumentEffects } from './store/domains/document/document.effects';
import { SendDocumentComponent } from './components/send-document-link/send-document-link.component';
import { UtilityService } from './services/utility.service';
import { AmountRendererComponent } from './components/invoices/renderer/amount/amount-renderer.component';
import { InvoiceNumberCellRendererComponent } from './components/invoices/renderer/invoice-number/invoice-number-cell-renderer.component';
import { InvoiceSelectionCheckboxRendererComponent } from './components/invoices/renderer/invoice-selection-checkbox-renderer.component';
import { PorContactDetailRendererComponent } from './components/contract-detail/contract-detail-renderer.component';
import { EventLogsEffects } from './store/app-wide/event-logs/event-logs.effects';
import { RedirectionEffects } from './store/app-wide/redirection/redirection.effects';
import { FormatCurrencyPipe } from './pipes/format-currency.pipe';
import { AdImagesComponent } from './components/ad-images/ad-images.component';

export const LOGIN_CONFIG = new InjectionToken('App Config');
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { EcomSharedModule } from '@por/ecom-shared';
import { OverlayModule } from '@angular/cdk/overlay';
import { ContractInstructionsComponent } from './components/contract-instructions/contract-instructions.component';
import { MultiPaymentContractPanelComponent } from './components/multi-payment-contract-panel/multi-payment-contract-panel.component';
import { MultiPaymentInvoicePanelComponent } from './components/multi-payment-invoice-panel/multi-payment-invoice-panel.component';
import { PaymentEffects } from './store/domains/payment/payment.effects';
import { ContractSelectionCheckboxRendererComponent } from './components/contracts/renderer/contract-selection-checkbox-renderer.component';
import { ContractEffects } from './store/domains/contract/contract.effects';
import { ItemsOutEffects } from './store/domains/itemsout/itemsout.effects';
import { PaymentSuccessDialogComponent } from './components/dialogs/payment-success-dialog/payment-success-dialog.component';
import { FormatAddressPipe } from './shared/pipes/format-address.pipe';
import { RequestSentSuccessfullyDialogComponent } from './components/dialogs/request-sent-successfully-dialog/request-sent-successfully-dialog.component';
import { PorPaymentComponent } from './payment/components/por-payment/por-payment.component';
import { ProductEffects } from './store/domains/product/product.effects';
import { ContractDetailRendererComponent } from './components/items-out/renderer/items-out-contract-detail-renderer/contract-detail-renderer.component';
import { AppEffects } from './store/app-wide/app/app.effects';
import { InvoicesEffects } from './store/domains/invoices/invoices.effects';
import { DocumentListsComponent } from './components/document-lists/document-lists.component';
import { PorIconModule } from '@por/shared/ui/icon-lib';
import { ContactsComponent } from './components/contacts/contacts.component';
import { PorContactDetailAmountDUeRendererComponent } from './components/contracts/renderer/amount-due/amount-due-cell-renderer.component';
import { OrganizationService } from './services/organization.service';
import { OrganizationEffects } from './store/domains/organization/organization.effects';
import { AccountFeatureEffects } from './store/domains/feature-account/account.effects';
import { PermissionCheckboxRendererComponent } from './components/contacts/renderer/permission-checkbox.component';
import { ContactActionsRendererComponent } from './components/contacts/renderer/contact-actions.component';
import { GoogleMapApiService } from './services/google-map-api.service';
import { ExternalProviderService } from './services/external-provider.service';
import { AssetTagComponent } from './components/asset-tag/asset-tag.component';
import { QuoteConversionComponent } from './components/quote-conversion/quote-conversion.component';
import { QuoteStepTrackerComponent } from './components/quote-step-tracker/quote-step-tracker.component';
import { DefaultPermissionsModalComponent } from './components/contacts/default-permissions-modal/default-permissions-modal.component';
import { AddContactModalComponent } from './components/contacts/add-contact-modal/add-contact-modal.component';
import { registerSupportedLocales } from './locale-data';

registerSupportedLocales();

@NgModule({
    declarations: [
        AccountSummaryComponent,
        AdImagesComponent,
        ContractsComponent,
        ItemsOutComponent,
        CloseItemOutComponent,
        TrackingComponent,
        InvoicesComponent,
        PageSidebarComponent,
        HeaderComponent,
        FooterComponent,
        CustomerPortalComponent,
        ConsumerRedirectionComponent,
        LanguageSelectorComponent,
        ItemsOutActionComponent,
        SubdomainRedirectionComponent,
        ContractFullWidthRowComponent,
        ItemsOutFullWidthRowComponent,
        InvoicesFullWidthRowComponent,
        HamburgerComponent,
        RentalRequestModalComponent,
        ContractPaymentHistoryModalComponent,
        ContractDetailComponent,
        PorContactDetailRendererComponent,
        FilterLineItemStatusPipe,
        FormLineItemNamePipe,
        ItemsOutDetailRendererComponent,
        InvoiceDetailComponent,
        FormatDatePipe,
        DepotTimezoneDatePipe,
        RequestCellRendererComponent,
        ItemsOutActionsMenuComponent,
        BasicConsumerPortalComponent,
        ServiceRequestComponent,
        ProductDetailComponent,
        QuickLinkComponent,
        BookAgainContractModalComponent,
        BookAgainContractRequestFormComponent,
        StringifyPipe,
        SelectedItemActionsComponent,
        SendDocumentComponent,
        AmountRendererComponent,
        InvoiceNumberCellRendererComponent,
        InvoiceSelectionCheckboxRendererComponent,
        ContractInstructionsComponent,
        MultiPaymentContractPanelComponent,
        MultiPaymentInvoicePanelComponent,
        ContractSelectionCheckboxRendererComponent,
        PaymentSuccessDialogComponent,
        PorPaymentComponent,
        ContractDetailRendererComponent,
        DocumentListsComponent,
        ContactsComponent,
        PorContactDetailAmountDUeRendererComponent,
        PermissionCheckboxRendererComponent,
        ContactActionsRendererComponent,
        AssetTagComponent,
        QuoteConversionComponent,
        QuoteStepTrackerComponent,
        DefaultPermissionsModalComponent,
        AddContactModalComponent
    ],
    exports: [LanguageSelectorComponent, ContactsComponent, DefaultPermissionsModalComponent, AddContactModalComponent],
    imports: [
        CommonModule,
        MaterialModule,
        RequestSentSuccessfullyDialogComponent,
        StoreModule.forRoot({
            ...store
        }),
        ...devModules,
        FormsModule,
        MatMenuModule,
        ReactiveFormsModule,
        MatNativeDateModule,
        NgxSkeletonLoaderModule,
        AgGridModule,
        SharedModule,
        ConsumerLoginModule,
        PaymentModule,
        PorFormsModule,
        ApxInputModule,
        ApxRadioModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: () => {
                    return new UITranslateLoader({
                        [Language.EN_US]: {
                            ...EnUSTranslations,
                            ...ConsumerLoginLanguage.enUSTranslations
                        },
                        [Language.EN_GB]: {
                            ...EnGBTranslations,
                            ...ConsumerLoginLanguage.enGBTranslations
                        },
                        [Language.EN_AU]: {
                            ...EnAUTranslations,
                            ...ConsumerLoginLanguage.enAUTranslations
                        },
                        [Language.ES_ES]: {
                            ...EsESTranslations,
                            ...ConsumerLoginLanguage.esESTranslations
                        },
                        [Language.FR_CA]: {
                            ...FrCATranslations,
                            ...ConsumerLoginLanguage.frCATranslations
                        },
                        [Language.FR_FR]: {
                            ...FrFRTranslations,
                            ...ConsumerLoginLanguage.frFRTranslations
                        }
                    });
                }
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: UIMissingTranslationHandler
            },
            /**
             * fallbackLang, NOT defaultLanguage: ngx-translate 17 deprecated `defaultLanguage`
             * and ignores it, leaving defaultLang null. Any route that does not explicitly call
             * TranslateService.use() then resolves NO keys and every string falls through
             * UIMissingTranslationHandler - raw keys in production. That is the embedded payment
             * component's situation: the host passes only {"paymentToken":"..."} and there is no
             * portal localStorage on the merchant's domain, so nothing supplies a language.
             */
            fallbackLang: DEFAULT_LOCALE
        }),
        ApxButtonModule,
        MatSnackBarModule,
        EcomSharedModule,
        EffectsModule.forRoot([
            CustomerEffects,
            DocumentEffects,
            EventLogsEffects,
            RedirectionEffects,
            AppEffects,
            InvoicesEffects,
            PaymentEffects,
            ItemsOutEffects,
            ContractEffects,
            ProductEffects,
            OrganizationEffects,
            AccountFeatureEffects
        ]),
        BsDropdownModule.forRoot(),
        OverlayModule,
        PorIconModule
    ],
    providers: [
        TrackService,
        HelpersService,
        GoogleMapApiService,
        ModalService,
        NotificationService,
        { provide: DateAdapter, useClass: AppDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BackendInterceptor,
            multi: true
        },
        Title,
        LoggerService,
        DatePipe,
        RedirectionService,
        LowerCasePipe,
        OrganizationIdService,
        OrganizationService,
        MapActionsService,
        {
            provide: LOGIN_CONFIG,
            useValue: loginDefaults
        },
        { provide: 'Window', useValue: window },
        CurrencyPipe,
        FormatNumberPipe,
        FormatDatePipe,
        DepotTimezoneDatePipe,
        UtilityService,
        FormatCurrencyPipe,
        FormatAddressPipe,
        ExternalProviderService,
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class CustomerPortalUiModule {}
